import { Injectable } from '@angular/core';
import { AngularFirestore, QuerySnapshot, DocumentData, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { isNoEmptyArray, isNullOrUndefined, isValidString } from '@pineapplelab/util';
import { PagingQueryOptions } from '../../models/pagingQueryOptions ';
import { DbService } from '../db/db.service';
import { environment } from '../../../environments/environment';
import { IFilter } from '../../models/filter';
import { getCountFromServer, collection, query as fsQuery, where, orderBy as fsOrderBy, getFirestore } from 'firebase/firestore';

@Injectable({
  providedIn: 'root'
})
export class PagingService {

  constructor(
	private firestore: AngularFirestore,
	private db: DbService
) { }

  private createBaseQuery(options: PagingQueryOptions, collection: AngularFirestoreCollection<DocumentData>) {
    let baseQuery: firebase.default.firestore.Query = collection.ref;

    if (isNullOrUndefined(options?.isDeletedFilter) || options?.isDeletedFilter) {
      baseQuery = baseQuery.where('is_deleted', '==', false);
    }

    if (options.searchText) {
      baseQuery = baseQuery
        .orderBy(options.orderBy)
        .startAt(options.searchText)
        .endAt(options.searchText + '\uf8ff');
    } else {
      baseQuery = baseQuery.orderBy(options.orderBy, options.orderDirection);
    }

    if (!isNullOrUndefined(options.filters)) {
      const filtersArray = Array.isArray(options.filters) ? options.filters : [options.filters];
      filtersArray.forEach(filter => {
		if (isValidString(filter?.operator)) {
			baseQuery = baseQuery.where(filter.property, filter.operator, filter.value);
		} else {
			baseQuery = filter.isArray
			  ? baseQuery.where(filter.property, 'array-contains', filter.value)
			  : baseQuery.where(filter.property, '==', filter.value);
		}
      });
    }

    return baseQuery;
  }

  /**
   * `lookahead` fetches one extra document so the caller can tell whether another page exists
   * without running a count over the whole collection. The extra doc must be discarded by the caller.
   */
  async getFirstPage(options: PagingQueryOptions, lookahead: boolean = false): Promise<QuerySnapshot<DocumentData>> {
    const collection = this.firestore.collection(options.path);
    const query = this.createBaseQuery(options, collection).limit(options.pageSize + (lookahead ? 1 : 0));
    return query.get();
  }

  async getNextPage(options: PagingQueryOptions, lookahead: boolean = false): Promise<QuerySnapshot<DocumentData>> {
    const collection = this.firestore.collection(options.path);
    const query = this.createBaseQuery(options, collection).startAfter(options.lastVisible).limit(options.pageSize + (lookahead ? 1 : 0));
    return query.get();
  }

  async getPrevPage(options: PagingQueryOptions): Promise<QuerySnapshot<DocumentData>> {
    const collection = this.firestore.collection(options.path);
    const query = this.createBaseQuery(options, collection).endBefore(options.firstVisible).limitToLast(options.pageSize);
    return query.get();
  }
  
  async getPage(options: PagingQueryOptions): Promise<QuerySnapshot<DocumentData>> {
    const collection = this.firestore.collection(options.path);
    const initialQuery = this.createBaseQuery(options, collection).limit((options.pageNumber! - 1) * options.pageSize + options.pageSize);
    const snapshot = await initialQuery.get();
    const startAtDoc = snapshot.docs[(options.pageNumber! - 1) * options.pageSize] ?? null;
    let pagedQuery = this.createBaseQuery(options, collection).limit(options.pageSize);
    if (startAtDoc) {
      pagedQuery = pagedQuery.startAt(startAtDoc);
    }
    return pagedQuery.get();
  }

  async getTotalDocumentsCount(options: PagingQueryOptions): Promise<number> {
    const db = getFirestore();
    let q = fsQuery(collection(db, options.path));
  
    if (isNullOrUndefined(options?.isDeletedFilter) || options?.isDeletedFilter) {
      q = fsQuery(q, where('is_deleted', '==', false));
    }
  
    if (!isNullOrUndefined(options.filters)) {
      const filtersArray = Array.isArray(options.filters) ? options.filters : [options.filters];
      filtersArray.forEach(filter => {
        const operator = isValidString(filter?.operator)
          ? filter.operator
          : filter.isArray
            ? 'array-contains'
            : '==';
        q = fsQuery(q, where(filter.property, operator as any, filter.value));
      });
    }
  
    if (options.searchText) {
      q = fsQuery(q, fsOrderBy(options.orderBy));
      q = fsQuery(q,
        where(options.orderBy, '>=', options.searchText),
        where(options.orderBy, '<=', options.searchText + '\uf8ff')
      );
    } else {
      q = fsQuery(q, fsOrderBy(options.orderBy));
    }
  
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }
  
  async execute(options: PagingQueryOptions, isDeleted: boolean = false): Promise<QuerySnapshot<DocumentData>> {
	if (!options?.path || !options?.orderBy || !options?.orderDirection) {
		return null;
	}

	const [,,collection] = options.path.split('/')

	let query = this.db.getProyIdRef(environment.projectId).collection(collection).orderBy(options?.orderBy, options?.orderDirection);
	if (options?.searchText) {
		query = query.where(options?.orderBy, '>=', options?.searchText).where(options?.orderBy, '<=', `${options?.searchText}\uf8ff`)
	}
	const normalizedFilter = (options?.filters && Array.isArray(options?.filters) ? options?.filters : [options?.filters]) as IFilter[];

	if (isNoEmptyArray(normalizedFilter)) {
		normalizedFilter.forEach(filter => {
			const operator = isValidString(filter?.operator) ? filter?.operator : (filter?.isArray ? 'array-contains' : '==');
			query = query.where(filter?.property, operator, filter?.value);
		});
	}

	query = query.where('is_deleted', '==', isDeleted);
	const results = await query.get();
	return results;
  }

}
